import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toastr } from "react-redux-toastr";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AuthService from "Lib/API/Auth";
import { useApiAction } from "hook/useFetchData";
import { getLocalStorageItem, getAccessTokenSilently, setLocalStorageItem } from "utils/functions";
import { setLogoImage } from "services/api/organization";
import { Fonts, FontFamilies, FontWeights, FontColors } from "theme/fontConfig";

const EmailSchema = Yup.object().shape({
  email: Yup.string().trim().email("Enter a valid email address").required("Email is required"),
});

const OtpSchema = Yup.object().shape({
  otp: Yup.array()
    .of(Yup.string().matches(/^[0-9]$/, "Must be a digit").required("Required"))
    .min(6, "Enter 6 digits"),
});

const LoginModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const logoImg = localStorage.getItem("logoImgForm") || getLocalStorageItem("getOrginasitionBySubDomain", true)?.logoImg;
  const orgName = localStorage.getItem("orgName") || getLocalStorageItem("getOrginasitionBySubDomain", true)?.orgName;
  const inputRefs = useRef([]);

  const [step, setStep] = useState("email");
  const [otpToken, setOtpToken] = useState("");
  const [sentEmail, setSentEmail] = useState("");
  const [emailForDisplay, setEmailForDisplay] = useState("");
  const [coolDown, setCoolDown] = useState(30);
  const [resendCount, setResendCount] = useState(0);
  const [emailError, setEmailError] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);

  const { execute: login } = useApiAction((body) => AuthService.loginWithOtp(body), false);
  const { execute: otpVerify } = useApiAction((body) => AuthService.otpVerify(body), false);
  const { execute: sendCode } = useApiAction((body) => AuthService.loginWithOtp(body), false);

  useEffect(() => {
    if (!open) {
      setStep("email");
      setOtpToken("");
      setSentEmail("");
      setEmailForDisplay("");
      setEmailError(null);
      setOtpError(null);
      setCoolDown(30);
      setResendCount(0);
    }
  }, [open]);

  useEffect(() => {
    if (step === "otp") setCoolDown(30);
  }, [step]);

  useEffect(() => {
    if (coolDown > 0) {
      const timer = setInterval(() => setCoolDown((p) => p - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [coolDown]);

  const handleResend = async () => {
    setResendLoading(true);
    try {
      const res = await sendCode({ email: sentEmail });
      setOtpToken(res?.result?.token);
      setEmailForDisplay(res?.result?.email);
      setResendCount((p) => p + 1);
      setCoolDown(60);
    } catch (err) {
      setOtpError(err?.data?.message);
      setTimeout(() => setOtpError(null), 7000);
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpChange = (e, index, setFieldValue) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setFieldValue(`otp[${index}]`, value);
    if (value && index < 5 && inputRefs.current[index + 1]) inputRefs.current[index + 1].focus();
  };

  const handleOtpKeyDown = (e, index, values, setFieldValue) => {
    if (e.key === "Backspace") {
      if (values.otp[index]) {
        setFieldValue(`otp[${index}]`, "");
      } else if (index > 0) {
        if (inputRefs.current[index - 1]) inputRefs.current[index - 1].focus();
        setFieldValue(`otp[${index - 1}]`, "");
      }
    }
  };

  const handleVerifySuccess = async (res) => {
    setLocalStorageItem("userAuthData", res?.result);
    localStorage.setItem("memberPortal", "true");
    toastr.success("Success", "Login successfully!");
    const token = await getAccessTokenSilently();
    try {
      const userRes = await AuthService.getUserExist(token);
      localStorage.setItem("paymentMethod", JSON.stringify(userRes?.user?.paymentMethodCreditCard));
      localStorage.setItem("checkUser", JSON.stringify(userRes?.exists));
      localStorage.setItem("accountID", userRes?.user?.permissions?.linked_account?._id);
      localStorage.setItem("accountName", userRes?.user?.permissions?.linked_account?.family_name);
      const accountInfo = {
        firstName: userRes?.user?.permissions?.linked_contact?.first_name,
        lastName: userRes?.user?.permissions?.linked_contact?.last_name,
        primary_email_address: userRes?.user?.permissions?.linked_contact?.email_address,
        emergency_contact_name: userRes?.user?.permissions?.linked_contact?.emergency_contact_name,
        emergency_contact_number: userRes?.user?.permissions?.linked_contact?.emergency_contact_number,
        profileImage: userRes?.user?.permissions?.linked_contact?.profile_img,
      };
      localStorage.setItem("accountInfo", JSON.stringify(accountInfo));
      localStorage.setItem("organization", JSON.stringify(userRes?.user?.permissions?.organization));
      localStorage.setItem("memberSetting", JSON.stringify(userRes?.user?.permissions?.organization?.memberSetting || {}));
      localStorage.setItem("memberPortalDirectory", JSON.stringify(userRes?.user?.permissions?.organization?.memberPortalDirectory || false));
      localStorage.setItem("linkedAccount", JSON.stringify(userRes?.user?.permissions?.linked_account));
      localStorage.setItem("hasPayment", userRes?.user?.hasPayment);
      localStorage.setItem("logoImg", userRes?.user?.permissions?.organization?.logoImg);
      localStorage.setItem("orgId", userRes?.user?.permissions?.organization?._id);
      if (userRes?.user?.permissions?.organization?.logoImg) {
        dispatch(setLogoImage(userRes?.user?.permissions?.organization?.logoImg));
      }
      localStorage.setItem("orgName", userRes?.user?.permissions?.organization?.orgName);
      onSuccess({
        first_name: userRes?.user?.permissions?.linked_contact?.first_name,
        last_name: userRes?.user?.permissions?.linked_contact?.last_name,
        primary_email_address: userRes?.user?.permissions?.linked_contact?.email_address,
      });
    } catch (err) {
      localStorage.setItem("checkUser", err?.data?.exists);
      onSuccess({});
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ p: 4, position: "relative" }}>
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>

        {logoImg && (
          <Box display="flex" justifyContent="center" mb={1} minHeight={70}>
            <img src={logoImg} alt="logo" loading="lazy" width={160} />
          </Box>
        )}
        <Typography variant="h6" fontWeight="bold" align="center" sx={{ fontFamily: Fonts.GeneralHeaderFont.fontFamily, fontSize: Fonts.GeneralHeaderFont.fontSize, mb: 0.5 }}>
          {orgName}
        </Typography>

        {step === "email" && (
          <>
            <Typography variant="body2" align="center" sx={{ fontFamily: Fonts.GeneralLargeBodyFont.fontFamily, mt: 1, fontSize: Fonts.GeneralLargeBodyFont.fontSize, color: FontColors.BlackAlpha54, mb: 0 }}>
              Enter your email to sign in to your account
            </Typography>
            <Formik
              initialValues={{ email: "" }}
              validationSchema={EmailSchema}
              onSubmit={async (values) => {
                try {
                  const res = await login({ email: values.email });
                  setOtpToken(res?.result?.token);
                  setSentEmail(values.email);
                  setEmailForDisplay(res?.result?.email);
                  toastr.success("Success", "Otp sent successfully. Please check your email!");
                  setStep("otp");
                } catch (err) {
                  setEmailError(err?.data?.message);
                  setTimeout(() => setEmailError(null), 7000);
                }
              }}
            >
              {({ values, errors, touched, handleBlur, handleChange, isSubmitting }) => (
                <Form>
                  <Box display="flex" justifyContent="center" sx={{ gap: { xs: 0.5, sm: 1, md: 1.5 } }} mb={2} mt={2}>
                    <TextField
                      name="email"
                      placeholder="yours@example.com"
                      variant="outlined"
                      fullWidth
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={(touched.email && Boolean(errors.email)) || !!emailError}
                      helperText={(touched.email && errors.email) || emailError}
                      sx={{
                        "& .MuiInputBase-input": { fontFamily: FontFamilies.General },
                        "& .MuiFormHelperText-root": { fontFamily: Fonts.GeneralCaptionFont.fontFamily, fontSize: Fonts.GeneralCaptionFont.fontSize, marginLeft: 0 },
                      }}
                    />
                  </Box>
                  <Box sx={{ flexDirection: "column", mt: 2, display: "flex", justifyContent: "center" }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={isSubmitting}
                      sx={{ borderRadius: 2, px: 4, textTransform: "none", ...Fonts.GeneralSubHeaderFont, backgroundColor: "#1675e0", width: "100%" }}
                    >
                      Send Code
                      {isSubmitting && <CircularProgress sx={{ color: FontColors.Blue1976D2, ml: 1 }} size={15} thickness={4} />}
                    </Button>
                  </Box>
                  <Typography variant="body2" align="center" sx={{ fontFamily: Fonts.GeneralLargeBodyFont.fontFamily, mt: 2, fontSize: Fonts.GeneralLargeBodyFont.fontSize, color: FontColors.BlackAlpha54 }}>
                    By logging in, you agree to our terms of service and privacy policy.
                  </Typography>
                </Form>
              )}
            </Formik>
          </>
        )}

        {step === "otp" && (
          <>
            <Typography variant="body2" align="center" sx={{ fontFamily: Fonts.GeneralLargeBodyFont.fontFamily, mb: 2, mt: 1, fontSize: Fonts.GeneralLargeBodyFont.fontSize, color: FontColors.BlackAlpha54 }}>
              To access your membership portal, please enter the code sent to your email: <br />
              <Box component="span" sx={{ fontWeight: FontWeights.SemiBold }}>{emailForDisplay}</Box>
            </Typography>
            <Formik
              initialValues={{ otp: ["", "", "", "", "", ""] }}
              validationSchema={OtpSchema}
              onSubmit={async (values) => {
                const code = values.otp.join("");
                try {
                  const res = await otpVerify({ code, token: otpToken });
                  await handleVerifySuccess(res);
                } catch (err) {
                  setOtpError(err?.data?.message);
                  setTimeout(() => setOtpError(null), 7000);
                }
              }}
            >
              {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                <Form>
                  <Box display="flex" justifyContent="center" sx={{ gap: { xs: 0.5, sm: 1, md: 1.5 } }} mb={2}>
                    {values.otp.map((val, index) => (
                      <Field key={index} name={`otp[${index}]`}>
                        {() => (
                          <TextField
                            inputRef={(el) => (inputRefs.current[index] = el)}
                            value={val}
                            onChange={(e) => handleOtpChange(e, index, setFieldValue)}
                            onKeyDown={(e) => handleOtpKeyDown(e, index, values, setFieldValue)}
                            onPaste={(e) => {
                              e.preventDefault();
                              const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
                              if (!pasted) return;
                              const otpArray = [...values.otp];
                              for (let i = 0; i < pasted.length && index + i < 6; i++) {
                                otpArray[index + i] = pasted[i];
                              }
                              otpArray.forEach((digit, i) => setFieldValue(`otp[${i}]`, digit));
                              const lastIdx = Math.min(index + pasted.length - 1, 5);
                              if (inputRefs.current[lastIdx]) inputRefs.current[lastIdx].focus();
                            }}
                            inputProps={{ maxLength: 1, style: { textAlign: "center", ...Fonts.GeneralHeaderFont, width: "45px" } }}
                            variant="outlined"
                            error={touched.otp && Boolean(errors.otp) && values.otp[index] === ""}
                          />
                        )}
                      </Field>
                    ))}
                  </Box>
                  {((touched.otp && errors.otp) || otpError) && (
                    <Typography color="error" variant="caption" align="center" display="block" sx={{ fontFamily: Fonts.GeneralLargeBodyFont.fontFamily, fontSize: Fonts.GeneralLargeBodyFont.fontSize }}>
                      {otpError || "OTP Required"}
                    </Typography>
                  )}
                  <Box sx={{ flexDirection: "column", mt: 2, display: "flex", justifyContent: "center" }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={isSubmitting}
                      sx={{ borderRadius: 2, px: 4, textTransform: "none", ...Fonts.GeneralSubHeaderFont, backgroundColor: "#1675e0", width: "100%" }}
                    >
                      Verify
                      {isSubmitting && <CircularProgress sx={{ color: FontColors.Blue1976D2, ml: 1 }} size={15} thickness={4} />}
                    </Button>
                  </Box>
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Button
                      variant="text"
                      size="small"
                      disabled={coolDown > 0 || resendLoading}
                      onClick={handleResend}
                      sx={{ textTransform: "none", fontFamily: Fonts.GeneralButtonFont.fontFamily, fontSize: Fonts.GeneralButtonFont.fontSize }}
                    >
                      {coolDown > 0 ? `Resend code in ${coolDown}s` : "Resend code"}
                      {resendLoading && <CircularProgress sx={{ ml: 1 }} size={12} />}
                    </Button>
                  </Box>
                  <Box display="flex" justifyContent="center" mt={1}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setStep("email")}
                      sx={{ textTransform: "none", fontFamily: Fonts.GeneralButtonFont.fontFamily, fontSize: Fonts.GeneralButtonFont.fontSize, color: FontColors.Blue1976D2 }}
                    >
                      Back to login
                    </Button>
                  </Box>
                  <Typography variant="body2" align="center" sx={{ fontFamily: Fonts.GeneralLargeBodyFont.fontFamily, mt: 2, fontSize: Fonts.GeneralLargeBodyFont.fontSize, color: FontColors.BlackAlpha54 }}>
                    By logging in, you agree to our terms of service and privacy policy.
                  </Typography>
                </Form>
              )}
            </Formik>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
